/**
 * Shape autopilot (S6) — maps a preview result's shape to tile suggestions.
 * Pure: column categories come from the charts feature's type detection
 * (dataType first, sampled values as fallback), so the dialog, templates
 * (S7) and AI source (S9) all suggest identically.
 */
import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';
import type { QueryColumn } from '../../../../types/queryExecution';
import type { ChartConfig } from '../../charts/types/chartTypes';
import { analyzeColumns } from '../../charts/utils/columnTypeDetection';
import { detectSpatialColumnNames } from './spatialShapeDetect';

export type ResultShape = {
  columns: QueryColumn[];
  rows: Array<Record<string, unknown>>;
  /**
   * Source engine of the preview — enables spatial detection (map
   * suggestion). Optional: callers without one (pin/AI paths today) simply
   * never get a map suggestion.
   */
  engine?: DatabaseEngineId;
};

export type TileSuggestion =
  | { tileType: 'chart'; chartConfig: ChartConfig; reason: string }
  | {
      tileType: 'stat';
      valueColumn?: string;
      sparkline?: { timeColumn: string; valueColumn: string };
      reason: string;
    }
  | { tileType: 'table'; reason: string }
  | {
      tileType: 'pivot';
      rowColumn: string;
      columnColumn: string;
      valueColumn: string;
      reason: string;
    }
  | { tileType: 'map'; geometryColumn?: string; reason: string };

export type ShapeSuggestions = {
  best: TileSuggestion;
  alternatives: TileSuggestion[];
  /** 0 rows but real columns — a healthy-empty monitoring result, not an error. */
  emptyWithValidShape: boolean;
};

const chart = (
  type: ChartConfig['type'],
  xColumn: string,
  yColumn: string,
  reason: string,
): TileSuggestion => ({
  tileType: 'chart',
  chartConfig: {
    type,
    xAxis: { column: xColumn },
    yAxis: [{ column: yColumn, aggregation: 'sum' }],
    showLegend: false,
    showGrid: type !== 'pie',
  },
  reason,
});

const table = (reason: string): TileSuggestion => ({ tileType: 'table', reason });

/** Pie slices stop reading well beyond a dozen categories. */
const PIE_MAX_ROWS = 12;

export function shapeSuggest(shape: ResultShape): ShapeSuggestions {
  const emptyWithValidShape = shape.columns.length > 0 && shape.rows.length === 0;
  if (shape.columns.length === 0 || emptyWithValidShape) {
    return {
      best: table('Shows any result set — including a healthy empty one'),
      alternatives: [],
      emptyWithValidShape,
    };
  }

  // Geometry present → this is a map (engine required for detection). The
  // non-spatial suggestion chain still runs so the other type cards keep
  // their auto-configs as alternatives.
  const spatialColumns = detectSpatialColumnNames(shape.columns, shape.rows, shape.engine);
  if (spatialColumns.length > 0) {
    const rest = suggestNonSpatial(shape);
    return {
      best: {
        tileType: 'map',
        geometryColumn: spatialColumns[0],
        reason: 'Geometry column detected — plot the rows on a map',
      },
      alternatives: [rest.best, ...rest.alternatives],
      emptyWithValidShape: false,
    };
  }

  return suggestNonSpatial(shape);
}

function suggestNonSpatial(shape: ResultShape): ShapeSuggestions {
  const infos = analyzeColumns(shape.columns, shape.rows);
  const numeric = infos.filter((c) => c.category === 'numeric');
  const dates = infos.filter((c) => c.category === 'date');
  const categorical = infos.filter((c) => c.category === 'string' || c.category === 'boolean');
  const fallbackTable = table('Shows every column as returned');

  // Single row + a numeric → this is a metric, not a data set.
  if (shape.rows.length === 1 && numeric.length >= 1) {
    return {
      best: {
        tileType: 'stat',
        valueColumn: numeric[0].key,
        reason: 'One row with a numeric value reads best as a single stat',
      },
      alternatives: [fallbackTable],
      emptyWithValidShape: false,
    };
  }

  // Time series: date + numeric.
  if (dates.length >= 1 && numeric.length >= 1) {
    const time = dates[0].key;
    const value = numeric[0].key;
    return {
      best: chart('line', time, value, 'Time column + numeric value — a classic trend line'),
      alternatives: [
        chart('bar', time, value, 'Bars per time bucket'),
        {
          tileType: 'stat',
          valueColumn: value,
          sparkline: { timeColumn: time, valueColumn: value },
          reason: 'Latest value with the trend as a sparkline',
        },
        fallbackTable,
      ],
      emptyWithValidShape: false,
    };
  }

  // Categorical breakdown: category(ies) + numeric.
  if (categorical.length >= 1 && numeric.length >= 1) {
    const x = categorical[0].key;
    const value = numeric[0].key;
    const alternatives: TileSuggestion[] = [];
    if (categorical.length >= 2) {
      alternatives.push({
        tileType: 'pivot',
        rowColumn: categorical[0].key,
        columnColumn: categorical[1].key,
        valueColumn: value,
        reason: 'Two categories + a value — cross-tabulate them',
      });
    }
    if (shape.rows.length <= PIE_MAX_ROWS) {
      alternatives.push(chart('pie', x, value, 'Share of the total per category'));
    }
    alternatives.push(fallbackTable);
    return {
      best: chart('bar', x, value, 'Category + numeric value — compare with bars'),
      alternatives,
      emptyWithValidShape: false,
    };
  }

  // Nothing chartable — the table renders anything.
  return { best: fallbackTable, alternatives: [], emptyWithValidShape: false };
}
